import { useCallback } from 'react'
import type { StandardCart, StandardOrder } from '@/types/ShopFront/CheckoutStandards'
import { getCart } from '@/services/Cart/getCart'
import { getOrder } from '@/services/FramedCheckout/orderServices/getOrder'
import { deleteCart, attemptToRecreateCart } from '@/services/FramedCheckout/orderServices/recreateOrder'
import { error, log } from '@/services/Log'
import { usePromise } from '@/hooks/usePromise'
import { pdpCheckoutEvent } from '../../helpers/GAEvents'

export type ProductAdder = () => (
  Promise<{
    success: boolean,
    newCart: StandardCart | null,
  }>
)

const prepareNewCheckoutForPDPItem = async ({
  addProductFunction,
}: {
  addProductFunction: ProductAdder
}): Promise<{
  newOrder: StandardOrder
  initialCart: StandardCart | null
}> => {
  const initialCart = await getCart()
  if (initialCart && initialCart.id) {
    await deleteCart(initialCart.id)
  }
  const addPdpItemResponse = await addProductFunction()
  if (!addPdpItemResponse.success || !addPdpItemResponse.newCart) {
    throw new Error('Failed to add PDP item to cart')
  }
  const newOrder = await getOrder({ cart: addPdpItemResponse.newCart })
  return {
    newOrder,
    initialCart,
  }
}

const recreateInitialCartWith = (
  initialCartPromise: Promise<{ initialCart: StandardCart | null }>,
) => (
  setCart: (newCart: StandardCart | null) => void,
) => async () => {
  log('recreateCart')
  try {
    const { initialCart } = await initialCartPromise
    log('recreateCart', { initialCart })
    const currentCart = await getCart()
    log('recreateCart', { currentCart })
    if (currentCart && currentCart.id) {
      log('Deleting cart with id', currentCart.id)
      await deleteCart(currentCart.id)
    }
    if (initialCart) {
      log('Attempting to recreate cart', initialCart)
      await attemptToRecreateCart(initialCart.lineItems)
    }
  } catch (err) {
    error(`The PDP Failed to restore original cart: ${String(err)}`, err)
  } finally {
    setCart(await getCart())
  }
}

type InitialCartHolder = {
  initialCart: StandardCart | null
}
const usePDPCheckout = ({
  addProductFunction,
  setCart,
}: {
  addProductFunction: ProductAdder,
  setCart: (newCart: StandardCart | null) => void,
}) => {
  const {
    promise: cartPrepPromise,
    resolve: resolvePrepPromise,
    reject: rejectPrepPromise,
  } = usePromise<InitialCartHolder>()

  const onStartCheckout = useCallback(async () => {
    log('onStartCheckout', { addProductFunction, rejectPrepPromise, resolvePrepPromise })
    pdpCheckoutEvent('paypal')
    if (!resolvePrepPromise || !rejectPrepPromise) {
      throw new Error('resolvePrepPromise or rejectPrepPromise not set')
    }
    const prepNewSingleItemCheckoutPromise = prepareNewCheckoutForPDPItem({
      addProductFunction,
    })
    prepNewSingleItemCheckoutPromise
      .then((...args) => {
        log('prepNewSingleItemCheckoutPromise resolved', ...args)
        resolvePrepPromise(...args)
      })
      .catch(rejectPrepPromise)
    const { newOrder } = await prepNewSingleItemCheckoutPromise
    return newOrder.cart
  }, [addProductFunction, rejectPrepPromise, resolvePrepPromise])

  return {
    onStartCheckout,
    beforeAbort: recreateInitialCartWith(cartPrepPromise)(setCart),
  }
}

export default usePDPCheckout
